import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { ArticleRow, SectionTitle } from "@/components/ArticleCards";
import { ARTICLE_TYPES } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

export default function Articles() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "");
  const [specialty, setSpecialty] = useState(params.get("specialty") || "");
  const [year, setYear] = useState("");
  const [vol, setVol] = useState("");
  const [issue, setIssue] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/articles", { params: { limit: 200 } }).then((r) => {
      setArticles(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      if (q && !(`${a.title} ${a.abstract || ""} ${(a.keywords || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if (type && a.article_type !== type) return false;
      if (specialty && a.main_specialty !== specialty) return false;
      if (year && a.year !== year) return false;
      if (vol && a.volume !== vol) return false;
      if (issue && a.issue !== issue) return false;
      return true;
    });
  }, [articles, q, type, specialty, year, vol, issue]);

  const years = useMemo(() => Array.from(new Set(articles.map((a) => a.year).filter(Boolean))).sort((a, b) => b.localeCompare(a)), [articles]);
  const volumes = useMemo(() => Array.from(new Set(articles.map((a) => a.volume).filter(Boolean))).sort(), [articles]);
  const issues = useMemo(() => Array.from(new Set(articles.map((a) => a.issue).filter(Boolean))).sort(), [articles]);
  const specialties = useMemo(() => Array.from(new Set(articles.map((a) => a.main_specialty).filter(Boolean))).sort(), [articles]);

  return (
    <div className="py-16 md:py-24" data-testid="articles-page">
      <div className="max-w-4xl mx-auto px-6">
        <SectionTitle subtitle={t("articles.subtitle")}>
          {t("articles.title")}
        </SectionTitle>

        <div className="bg-[#F5F7FA] border border-gray-200 p-5 mb-8">
          <div className="flex items-center gap-2 text-[#001F3F] mb-4">
            <Filter className="w-4 h-4 text-[#2BA6A6]" />
            <span className="font-sans text-sm uppercase tracking-wider font-semibold">{t("home.filter_title")}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4 relative">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("home.filter_search_placeholder")} className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="articles-search" />
            </div>
            <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="md:col-span-4 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="articles-filter-specialty">
              <option value="">{t("home.filter_all_specialties")}</option>
              {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={type} onChange={(e) => setType(e.target.value)} className="md:col-span-4 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]">
              <option value="">{t("home.filter_all_types")}</option>
              {ARTICLE_TYPES.map((typ) => <option key={typ} value={typ}>{typ}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="md:col-span-4 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]">
              <option value="">{t("home.filter_all_years")}</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={vol} onChange={(e) => setVol(e.target.value)} className="md:col-span-4 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]">
              <option value="">{t("home.filter_vol_short")}</option>
              {volumes.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={issue} onChange={(e) => setIssue(e.target.value)} className="md:col-span-4 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]">
              <option value="">{t("home.filter_iss_short")}</option>
              {issues.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        <div data-testid="articles-results">
          {filtered.length === 0 ? (
            <div className="text-center py-20 font-serif text-[#001F3F]/60 text-lg border border-dashed border-gray-200">
              {loading ? t("home.loading") : t("home.empty_list")}
            </div>
          ) : (
            filtered.map((a) => <ArticleRow key={a.id} article={a} />)
          )}
        </div>
      </div>
    </div>
  );
}
